import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { adminService } from "../../services/adminService";
import { toast } from "sonner";
import { useState } from "react";
import { Loader2, RefreshCw, Info } from "lucide-react";
import { showErrorToast } from "@/utils/errorHandler";

const UpdateDownloadCount = () => {
  const [running, setRunning] = useState(false);

  const handleRecalculate = async () => {
    try {
      setRunning(true);
      const result = await adminService.recalculateUserDownloadCounts();
      if (result.success) {
        const detail =
          result.users_updated !== undefined && result.users_updated !== null
            ? ` ${result.users_updated} user(s) updated.`
            : "";
        toast.success(
          (result.message || "Download counts updated successfully.") + detail
        );
      } else {
        toast.error(result.message || "Update failed.");
      }
    } catch (error: unknown) {
      showErrorToast(
        error,
        "Failed to recalculate download counts. The backend may not support this yet."
      );
      console.error("Recalculate download counts error:", error);
    } finally {
      setRunning(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-foreground">
            Update Users Download Count
          </h2>
        </div>

        <div className="space-y-6 rounded-lg border border-border bg-card p-6">
          <div className="flex gap-3 rounded-lg border border-border bg-muted/30 p-4">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">
                This script summarizes user activity by updating each
                user&apos;s <strong>downcount</strong>.
              </p>
              <p>
                <strong>Downcount</strong> is the number of{" "}
                <em>unique files</em> a user has downloaded (not total
                downloads). For example: if a user downloaded file1.pdf three
                times and file2.pdf once, their downcount is <strong>2</strong>.
              </p>
              <p>
                The backend recomputes these counts from the user-download
                records (grouping by user and file/item) and writes the result
                into the user profile (or equivalent) table. Run this when you
                need to refresh stored counts after data changes or migrations.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Button
              onClick={handleRecalculate}
              disabled={running}
              className="bg-[#654321] text-white hover:bg-[#543210]"
            >
              {running ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Recalculating…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Recalculate download counts
                </>
              )}
            </Button>
            {running && (
              <span className="text-sm text-muted-foreground">
                This may take a moment for large datasets.
              </span>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UpdateDownloadCount;
